import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * «Формат» on the gift card page (blocks/cz-gift-card-format.liquid).
 *
 * With «друкована листівка» chosen, the add to cart button puts two items in the cart with one
 * request: the gift card exactly as the form would (recipient fields included, so Shopify e-mails
 * it within a minute) and the printed card in the matching design, carrying the recipient
 * name and greeting for the merchant to write on it. The send-on date is dropped: the card ships
 * now, so the e-mail goes now too.
 *
 * The submit is caught on window in the capture phase, before Horizon's delegated handler on
 * document, and the add uses the same cart event flow as the product form, so the drawer opens
 * and the cart icon updates.
 */

/** Recipient form property → what the merchant reads on the printed card's line. */
const CARD_LABELS = {
  'Recipient name': 'Імʼя отримувача',
  Message: 'Текст на листівці',
};

/** @returns {'digital' | 'print'} */
const currentFormat = () => (document.documentElement.dataset.czGcFormat === 'print' ? 'print' : 'digital');

class CzGiftCardFormat extends HTMLElement {
  connectedCallback() {
    // The section re-renders on variant change; keep the buyer's choice.
    const checked = this.querySelector(`input[value="${currentFormat()}"]`);
    if (checked instanceof HTMLInputElement) checked.checked = true;
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = (/** @type {Event} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    document.documentElement.dataset.czGcFormat = input.value;
    this.showError(false);
  };

  /** @param {string} giftVariantId */
  printVariantFor(giftVariantId) {
    for (const pair of (this.dataset.map || '').split(',')) {
      const [gift, print] = pair.split(':');
      if (gift === giftVariantId) return print;
    }
    return null;
  }

  /** @param {boolean} [visible] @param {string} [message] */
  showError(visible = true, message) {
    const error = this.querySelector('[data-error]');
    if (!(error instanceof HTMLElement)) return;
    if (!error.dataset.defaultText) error.dataset.defaultText = error.textContent?.trim() || '';
    error.textContent = message || error.dataset.defaultText;
    error.hidden = !visible;
  }
}

/** @param {HTMLFormElement} form */
function formProperties(form) {
  /** @type {Record<string, string>} */
  const gift = {};
  /** @type {Record<string, string>} */
  const card = {};
  for (const [name, value] of new FormData(form)) {
    const match = name.match(/^properties\[(.+)\]$/);
    if (!match || typeof value !== 'string') continue;
    const key = match[1];
    if (key === 'Send on') continue;
    gift[key] = value;
    if (key in CARD_LABELS && value.trim() !== '') {
      card[CARD_LABELS[/** @type {keyof typeof CARD_LABELS} */ (key)]] = value.trim();
    }
  }
  return { gift, card };
}

/** @param {SubmitEvent} event */
async function onSubmit(event) {
  if (currentFormat() !== 'print') return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const productForm = form.closest('product-form-component');
  const picker = document.querySelector('cz-gift-card-format');
  if (!productForm || !(picker instanceof CzGiftCardFormat)) return;

  const giftVariantId = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'))?.value;
  const printVariantId = giftVariantId ? picker.printVariantFor(giftVariantId) : null;
  if (!giftVariantId || !printVariantId) return; // no card for this denomination: plain gift card

  event.preventDefault();
  event.stopImmediatePropagation();
  if (!form.reportValidity()) return;

  const buttons = productForm.querySelectorAll('button[type="submit"]');
  buttons.forEach((button) => button.setAttribute('disabled', ''));

  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  productForm.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        { merchandiseId: giftVariantId, quantity: 1 },
        { merchandiseId: printVariantId, quantity: 1 },
      ],
      promise: deferred.promise,
    })
  );

  try {
    const { gift, card } = formProperties(form);
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          { id: Number(giftVariantId), quantity: 1, properties: gift },
          { id: Number(printVariantId), quantity: 1, properties: card },
        ],
        sections: sections.join(','),
      }),
    }).then((result) => result.json());
    if (response.status) throw Object.assign(new Error(response.message), { description: response.description });

    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
      (result) => result.json()
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cz-gift-card-format',
        itemCount: 2,
        sections: response.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.warn('[cz-gift-card-format]', error);
    deferred.reject(error);
    // Shopify's own message (e.g. an invalid recipient e-mail) is more useful than a generic one.
    const description = error && typeof error === 'object' && 'description' in error ? error.description : '';
    picker.showError(true, typeof description === 'string' ? description : '');
  } finally {
    buttons.forEach((button) => button.removeAttribute('disabled'));
  }
}

if (!customElements.get('cz-gift-card-format')) {
  customElements.define('cz-gift-card-format', CzGiftCardFormat);
  // window, not document: Horizon delegates submit on document in the capture phase too, and
  // window's capture listeners run first.
  window.addEventListener('submit', onSubmit, { capture: true });
}
